import React from 'react';
import { AppState, AppStateUpdate, SimState, UserInfo } from '../types';

interface Props {
  appState:    AppState;
  currentUser: UserInfo;
  templateDataUrl: string | null;
  infoMsg:         string | null;
  hasCutRegion:    boolean;
  suggestedMaxH:   number | null;
  onUpdate:    (update: AppStateUpdate) => void;
  onLogout:    () => void;
  onLoadTemplate:     () => void;
  onNewProj:          () => void;
  onLoadProj:         () => void;
  onSaveProj:         () => void;
  onLayers:           () => void;
  onTemLayers:        () => void;
  onTempDesign:       () => void;
  onParameters:       () => void;
  onResetView:        () => void;
  onSave3D:           () => void;
  onSave2D:           () => void;
  onDistortions:      () => void;
  onDistortionReset:  () => void;
  simState:           SimState;
  onSimStart:         () => void;
  onSimPause:         () => void;
  onSimStop:          () => void;
  onSimReset:         () => void;
  onSimRelax:         () => void;
  simDiagReady:       boolean;
  onDownloadSimLog:   () => void;
  simHmReady:         boolean;
  onDownloadHeightMap: () => void;
  meshReady:          boolean;
  onDownloadMesh:     () => void;
  onShowPreview?:     () => void;
  onExit:             () => void;
}

const LeftPanel: React.FC<Props> = ({
  appState, currentUser, templateDataUrl, infoMsg, hasCutRegion, suggestedMaxH, onUpdate, onLogout,
  onLoadTemplate, onNewProj, onLoadProj, onSaveProj,
  onLayers, onTemLayers, onTempDesign, onParameters, onResetView,
  onSave3D, onSave2D, onDistortions, onDistortionReset,
  simState, onSimStart, onSimPause, onSimStop, onSimReset, onSimRelax,
  simDiagReady, onDownloadSimLog, simHmReady, onDownloadHeightMap,
  meshReady, onDownloadMesh,
  onShowPreview, onExit,
}) => {
  const set = (key: keyof AppState, value: AppState[keyof AppState]) =>
    onUpdate({ [key]: value });

  return (
    <div className="left-panel">

      {/* ── Belka użytkownika ── */}
      <div className="user-bar">
        <span className="user-bar-name" title={currentUser.login}>
          {currentUser.fullName}
        </span>
        <button className="btn-logout-small" onClick={onLogout} title="Wyloguj">
          Wyloguj
        </button>
      </div>

      {/* ── Rząd 1: Load Template ── */}
      <button onClick={onLoadTemplate}>Load Template</button>

      {/* ── Rząd 2: New Proj | Load Proj | Save Proj ── */}
      <div className="btn-row-3 btn-row-3-eq">
        <button onClick={onNewProj}>New Proj</button>
        <button onClick={onLoadProj}>Load Proj</button>
        <button onClick={onSaveProj}>Save Proj</button>
      </div>

      {/* ── Rząd 2: Pic Layers | Tem Layers | Tem Design ── */}
      <div className="btn-row-3 btn-row-3-eq">
        <button onClick={onLayers} disabled={!currentUser.canPicLayers}
          title={currentUser.canPicLayers ? undefined : 'Brak uprawnień'}>
          Pic Layers
        </button>
        <button onClick={onTemLayers}>Tem Layers</button>
        <button onClick={onTempDesign}>Tem Design</button>
      </div>

      {/* ── Rząd 3: Parameters | Reset View ── */}
      <div className="btn-row-2">
        <button onClick={onParameters}>Parameters</button>
        <button onClick={onResetView}>Reset View</button>
      </div>

      {/* ── Rząd 4: Save 3D mod | Save 2D bmp ── */}
      <div className="btn-row-2">
        <button onClick={onSave3D}>Save 3D mod</button>
        <button onClick={onSave2D}>Save 2D bmp</button>
      </div>

      {/* ── Miniatura szablonu ── */}
      <div className="thumbnail-wrap">
        <div
          className={`thumbnail${templateDataUrl && onShowPreview ? ' thumbnail-clickable' : ''}`}
          title={templateDataUrl ? 'Click to preview template & mesh' : 'Template preview'}
          onClick={templateDataUrl ? onShowPreview : undefined}
        >
          {templateDataUrl
            ? <img src={templateDataUrl} alt="template" className="thumbnail-img" />
            : null
          }
        </div>
        {infoMsg && <div className="tpl-toast">{infoMsg}</div>}
      </div>

      {/* ── Nazwa i wersja aplikacji ── */}
      <div className="app-info">
        <div className="app-name">Picture Dome Designer</div>
        <div className="app-ver">ver260403.001</div>
      </div>

      {/* ── Informacja o materiale ── */}
      <div className="material-info">
        Material:&nbsp;
        {appState.materialWidth}&nbsp;mm&nbsp;×&nbsp;{appState.materialHeight}&nbsp;mm,&nbsp;
        curr. height&nbsp;=&nbsp;{appState.currentHeight.toFixed(1)}&nbsp;mm
      </div>

      {/* ── Opcje wyświetlania ── */}
      <div className="options-section">

        {/* Lewa kolumna: checkboxy */}
        <div className="checks-col">
          <div className="check-row">
            <label>
              <input type="checkbox" checked={appState.showGridCm}
                onChange={e => set('showGridCm', e.target.checked)} />
              Grid cm
            </label>
            <label>
              <input type="checkbox" checked={appState.showGridMm}
                onChange={e => set('showGridMm', e.target.checked)} />
              mm
            </label>
          </div>
          <label>
            <input type="checkbox" checked={appState.showMargin}
              onChange={e => set('showMargin', e.target.checked)} />
            Margin
          </label>
          <label>
            <input type="checkbox" checked={appState.negGMColor}
              onChange={e => set('negGMColor', e.target.checked)} />
            NegGMColor
          </label>
          <label title="Po osiągnięciu równowagi przez symulację rozciągnij Z do zadanej maksymalnej wysokości">
            <input type="checkbox" checked={appState.pullUpMaxH}
              onChange={e => set('pullUpMaxH', e.target.checked)} />
            Pull up max h
          </label>
          {hasCutRegion && (
            <label title="Pokaż pełny arkusz z obszarami do wycięcia (czerwonymi)">
              <input type="checkbox" checked={appState.showFullMaterial}
                onChange={e => set('showFullMaterial', e.target.checked)} />
              Show full mat.
            </label>
          )}
        </div>

        {/* Prawa kolumna: Distortions, Opening nav */}
        <div className="opening-col">

          {/* ── Wiersz deformacji ── */}
          <div className="dis-row">
            <button onClick={onDistortions} className="btn-dis" title="Zastosuj pre-deformację tekstury">
              Distortions
            </button>
            <select
              className="dis-ver-select"
              value={appState.distortionVersion}
              onChange={e => set('distortionVersion', Number(e.target.value))}
              title="Wersja algorytmu deformacji"
            >
              <option value={1}>1</option>
            </select>
            <input
              type="number"
              className="inp-dis-str"
              value={appState.distortionStrength}
              min={0} max={2} step={0.1}
              onChange={e => set('distortionStrength', Math.max(0, Math.min(2, Number(e.target.value))))}
              title="Siła deformacji (0–2)"
            />
            <button
              className="btn-dis-reset"
              onClick={onDistortionReset}
              title="Zresetuj deformacje"
            >↺</button>
          </div>

          <div className="opening-label">
            Opening&nbsp;{appState.currentOpening}&nbsp;/&nbsp;{appState.totalOpenings}
          </div>
          <div className="btn-row-2">
            <button
              onClick={() => set('currentOpening',
                appState.currentOpening <= 1 ? appState.totalOpenings : appState.currentOpening - 1)}
              disabled={appState.totalOpenings <= 1}
            >Prev</button>
            <button
              onClick={() => set('currentOpening',
                appState.currentOpening >= appState.totalOpenings ? 1 : appState.currentOpening + 1)}
              disabled={appState.totalOpenings <= 1}
            >Next</button>
          </div>
          <div className="check-row">
            <label>
              <input type="checkbox" checked={appState.showComputed}
                onChange={e => set('showComputed', e.target.checked)} />
              com.d.
            </label>
            <label title="Wyróżnij aktywny otwór żółtym konturem w widoku 3D">
              <input type="checkbox" checked={appState.showVisible}
                onChange={e => set('showVisible', e.target.checked)} />
              visib.
            </label>
          </div>
        </div>
      </div>

      {/* ── Max height ── */}
      <div className="max-height-section">
        <div className="max-height-label">
          Max height (&lt;=&nbsp;{suggestedMaxH ?? appState.maxBlowHeight}&nbsp;mm)
        </div>
        <div className="max-height-input-row">
          <input
            type="number"
            className="inp-number"
            value={appState.maxBlowHeight}
            min={1} max={999}
            onChange={e => set('maxBlowHeight', Math.max(1, Number(e.target.value)))}
          />
          <span className="unit-label">[mm]</span>
        </div>
      </div>

      {/* ── Separator + Simulation controls ── */}
      <div className="spacer" />
      <div className="sim-controls">
        <span className="sim-label">Simulation:</span>
        <button
          className="btn-sim-icon"
          title="Start"
          disabled={simState === 'running'}
          onClick={onSimStart}
        >▶</button>
        <button
          className="btn-sim-icon"
          title="Pause"
          disabled={simState !== 'running'}
          onClick={onSimPause}
        >⏸</button>
        <button
          className="btn-sim-icon"
          title="Stop"
          disabled={simState !== 'running' && simState !== 'paused'}
          onClick={onSimStop}
        >■</button>
        <button
          className="btn-sim-icon"
          title="Reset"
          disabled={simState === 'running'}
          onClick={onSimReset}
        >0</button>
      </div>

      {/* ── Test controls ── */}
      <div className="sim-controls">
        <span className="sim-label">Test:</span>
        <button
          className="btn-sim-icon"
          title="Pobierz log CSV z diagnostyką symulacji"
          disabled={!simDiagReady}
          onClick={onDownloadSimLog}
        >📊</button>
        <button
          className="btn-sim-icon"
          title="Pobierz mapę wysokości Z (PNG, skala szarości)"
          disabled={!simHmReady}
          onClick={onDownloadHeightMap}
        >HM</button>
        <button
          className="btn-sim-icon"
          title="Relaksacja: zablokuj wzrost ciśnienia i wyrównaj naprężenia w siatce"
          disabled={simState !== 'running' && simState !== 'paused'}
          onClick={onSimRelax}
        >Rlx</button>
        <button
          className="btn-sim-icon"
          title="Pobierz siatkę MES w formacie PDD_MESH_V1 (.pddm) dla symulatora C++"
          disabled={!meshReady}
          onClick={onDownloadMesh}
        >Sm</button>
        <input
          type="number"
          className="inp-pressure-frac"
          value={appState.pressureFraction}
          min={0.01} max={1} step={0.01}
          title="Ułamek ciśnienia (0.01–1.0): niższe ciśnienie → niższa końcowa wysokość kopuły"
          onChange={e => {
            const v = Math.max(0.01, Math.min(1.0, Number(e.target.value)));
            onUpdate({ pressureFraction: v });
          }}
        />
      </div>

      {/* ── Exit ── */}
      <button className="btn-exit" onClick={onExit}>
        Exit
      </button>

    </div>
  );
};

export default LeftPanel;
